import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';
import IErrorObject from '../../types/ErrorObject';
import './Field.scss';
import IFieldProps from './FieldProps';

const Field = <T, FormType extends { [field: string]: any }>({
  form,
  name,
  render,
  shouldValidateOnUpdate,
  defaultValue,
  validations,
  onChange,
  className,
}: IFieldProps<T, FormType>) => {
  const previousData = useRef<FormType>();

  useEffect(() => {
    if (shouldValidateOnUpdate) {
      if (previousData.current && shouldValidateOnUpdate(previousData.current)) {
        validateField(getValue());
      }
    } else {
      validateField(getValue());
    }
  }, [form.state.data]);

  useEffect(() => {
    previousData.current = form.state.data;
  }, [form.state.data]);

  useEffect(() => {
    const val = getValue();
    validateField(val);
  }, []);

  function getValue() {
    const formValue = form.state.data[name];
    return formValue === undefined ? defaultValue : formValue;
  }

  function handleChange(value: T, onChanged?: (v: T) => void) {
    if (onChanged) {
      onChanged(value);
    }

    if (onChange) {
      onChange(value);
    }

    validateField(value);
    form.handleChange(name, value as FormType[keyof FormType]);
  }

  async function validateField(value: T) {
    if (validations) {
      let errorObj = {};

      for (const v of validations) {
        let isValid = false;
        const args = (v.args as { message?: string }) || {};
        const validation = v.validation && v.validation(value, args, form.state.data);

        isValid = validation && (await validation.validate());

        if (isValid) {
          errorObj = {
            hasError: false,
            message: '',
          };
        } else {
          errorObj = {
            hasError: true,
            message:
              args.message ||
              validation.message ||
              'Validation objects must have both a validation and message property',
          };
          break;
        }
      }
      form.updateError(name, errorObj as IErrorObject);
    } else {
      form.updateError(name, { hasError: false, message: '' });
    }
  }

  const hasError = form.state.errors[name] ? form.state.errors[name].hasError : false;
  const errorMessage = hasError ? form.state.errors[name].message : '';
  const classes = classNames(
    {
      'form-field': true,
      'form-field-error': form.state.isDirty && hasError,
    },
    className
  );

  return (
    <div className={classes}>
      {render({
        errorMessage,
        hasError: hasError && form.state.isDirty === true,
        handleChange,
      })}
    </div>
  );
};

Field.defaultProps = {
  defaultValue: '',
  shouldValidateOnUpdate: () => true,
};

export default Field;
